/**
 * SEO ENGINE FILE - builds every page's `<head>`: title, description, robots
 * switch, canonical, Open Graph/Twitter, and ONE JSON-LD `@graph` with a single
 * business identity referenced by `@id`.
 *
 * 🔒 Do NOT let the AI builder (Lovable) edit this file. See AGENTS.md.
 *
 * Usage in a route:
 *   head: () => buildHead({ title, description, path: "/about" })
 */

import { SITE, SITE_URL, INDEXABLE, absoluteUrl } from "./site-config";
import ogDefault from "../assets/og-default.png";

/** Branded 1200x630 OG card used when a page doesn't provide its own image. */
const OG_DEFAULT_IMAGE = ogDefault;
const OG_IMAGE_WIDTH = "1200";
const OG_IMAGE_HEIGHT = "630";
const OG_IMAGE_ALT = `${SITE.name} - ${SITE.description}`;

/** Stable @id for the one business identity node, reused on every page. */
export const ORG_ID = `${SITE_URL}/#organization`;

type Json = Record<string, unknown>;

/* -------------------------------------------------------------------------- */
/*  Schema (@graph) nodes                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The single business identity. Every page references this by `@id` instead of
 * repeating the NAP. Includes real address, hours, and service area. Geo and
 * `sameAs` are emitted only once they hold real values (see site-config TODOs).
 *
 * ⛔ Never add `aggregateRating`/`review` here - real reviews live off-site on
 *    the Google Business Profile (Phase 6), never as invented schema.
 */
export function organizationNode(): Json {
  const node: Json = {
    "@type": ["FinancialService", "LocalBusiness"],
    "@id": ORG_ID,
    name: SITE.name,
    legalName: SITE.legalName,
    description: SITE.description,
    url: SITE_URL,
    telephone: SITE.phoneE164,
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.address.street,
      addressLocality: SITE.address.locality,
      addressRegion: SITE.address.region,
      postalCode: SITE.address.postalCode,
      addressCountry: SITE.address.country,
    },
    areaServed: SITE.citiesServed.map((c) => ({ "@type": "City", name: c })),
    openingHoursSpecification: SITE.openingHours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
  };
  if (SITE.email) node.email = SITE.email;
  if (SITE.geo.latitude != null && SITE.geo.longitude != null) {
    node.geo = {
      "@type": "GeoCoordinates",
      latitude: SITE.geo.latitude,
      longitude: SITE.geo.longitude,
    };
  }
  if (SITE.licenseNumber) {
    node.identifier = {
      "@type": "PropertyValue",
      name: "State lending/broker license",
      value: SITE.licenseNumber,
    };
  }
  if (SITE.sameAs.length) node.sameAs = SITE.sameAs;
  return node;
}

/** A BreadcrumbList node from site-relative crumbs (paths made absolute). */
export function breadcrumbNode(crumbs: Array<{ name: string; path: string }>): Json {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}

/** An FAQPage node. Text must match the answers visible on the page. */
export function faqNode(faqs: Array<{ q: string; a: string }>): Json {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/** Reference the business identity from a page node (e.g. provider/publisher). */
export const orgRef = { "@id": ORG_ID } as const;

/* -------------------------------------------------------------------------- */
/*  buildHead()                                                                */
/* -------------------------------------------------------------------------- */

type MetaTag =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };
type LinkTag = { rel: string; href: string; [k: string]: string };
type ScriptTag = { type: string; children: string };

export type HeadResult = {
  meta: MetaTag[];
  links: LinkTag[];
  scripts: ScriptTag[];
};

export type BuildHeadInput = {
  /** Unique page title (~60 chars, main keyword + city first). */
  title: string;
  /** Unique meta description (~155 chars). */
  description: string;
  /** Site-relative path for canonical + og:url, e.g. "/dallas/". */
  path: string;
  /** Absolute or site-relative image URL for OG/Twitter cards. */
  image?: string;
  /** Open Graph type. Defaults to "website". */
  type?: "website" | "article";
  /** Force `noindex` for this page even on a live build (e.g. thin/templated pages). */
  noindex?: boolean;
  /** Breadcrumb trail (site-relative paths). */
  breadcrumbs?: Array<{ name: string; path: string }>;
  /** FAQ entries - must match the answer text visible on the page. */
  faqs?: Array<{ q: string; a: string }>;
  /** Extra @graph nodes (Service, CollectionPage, FinancialProduct, …). */
  graph?: Json[];
};

/**
 * Build a complete, SEO-correct `<head>` for a route. Returns `{ meta, links,
 * scripts }` ready to return from a TanStack `head()`.
 */
export function buildHead(input: BuildHeadInput): HeadResult {
  const canonical = absoluteUrl(input.path);
  const robots =
    INDEXABLE && !input.noindex
      ? "index, follow, max-image-preview:large, max-snippet:-1"
      : "noindex, nofollow";
  const image = absoluteUrl(input.image ?? OG_DEFAULT_IMAGE);
  const type = input.type ?? "website";

  const meta: MetaTag[] = [
    { title: input.title },
    { name: "description", content: input.description },
    { name: "robots", content: robots },
    { name: "author", content: SITE.name },
    { property: "og:site_name", content: SITE.name },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:type", content: type },
    { property: "og:url", content: canonical },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
  ];
  meta.push({ property: "og:image", content: image });
  meta.push({ property: "og:image:width", content: OG_IMAGE_WIDTH });
  meta.push({ property: "og:image:height", content: OG_IMAGE_HEIGHT });
  meta.push({ property: "og:image:alt", content: OG_IMAGE_ALT });
  meta.push({ name: "twitter:image", content: image });

  const graph: Json[] = [organizationNode()];
  if (input.breadcrumbs?.length) graph.push(breadcrumbNode(input.breadcrumbs));
  if (input.faqs?.length) graph.push(faqNode(input.faqs));
  if (input.graph?.length) graph.push(...input.graph);

  return {
    meta,
    links: [{ rel: "canonical", href: canonical }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }),
      },
    ],
  };
}
